<template>
  <div>
    <v-row>
      <page-title value="スタッフ設定"></page-title>
      <v-spacer />
      <div class="d-flex align-start">
        <v-checkbox
          v-model="isHiddenOnly"
          :disabled="loading"
          class="my-0"
          hide-details
          color="primary"
        >
        </v-checkbox>
        <h4 class="my-3 pt-1 mr-4 ml-0">非表示設定を表示</h4>
      </div>
      <div class="d-flex align-start">
        <v-checkbox
          v-model="check"
          :disabled="loading"
          class="my-0"
          hide-details
          color="primary"
        >
        </v-checkbox>
        <h4 class="my-3 pt-1 mr-4 ml-0">退職者を表示</h4>
      </div>
      <v-menu offset-y>
        <template v-slot:activator="{ props }">
          <v-btn
            class="mr-2 my-2"
            color="primary"
            variant="outlined"
            :disabled="loading"
            v-bind="props"
          >
            <v-icon start>mdi-tray-arrow-down</v-icon>
            ダウンロード
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            href="https://s3.ap-northeast-1.amazonaws.com/data.c-c-s.app/public/csv_format/import_staff_sample.csv"
          >
            <v-list-item-title>サンプルCSV</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        @click="importDialog = true"
        class="mr-2 my-2"
        color="primary"
        variant="outlined"
        :disabled="loading"
      >
        <v-icon start>mdi-plus</v-icon>
        一括インポート
      </v-btn>
      <v-btn @click="createStaff" color="primary" variant="flat" :disabled="loading" class="my-2">
        <v-icon start>mdi-plus</v-icon>
        作成
      </v-btn>
    </v-row>

    <div class="pt-10">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="my-0" />
      
      <v-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th class="text-left">氏名</th>
              <th class="text-left">氏名（カナ）</th>
              <th class="text-left">性別</th>
              <th class="text-left">配属（予定）日</th>
              <th class="text-left">退職（予定）日</th>
              <th class="text-left">担任クラス</th>
              <th class="text-left">編集</th>
            </tr>
          </thead>
          <draggable
            v-model="displayedStaff"
            tag="tbody"
            item-key="userNurseryId"
            handle=".drag-handle"
            animation="200"
            @end="onDragEnd"
          >
            <template #item="{ element: s }">
              <tr>
                <td class="text-start">
                  <v-icon class="drag-handle" size="small"> mdi-drag </v-icon>
                  {{ s.lastName + s.firstName }}
                </td>
                <td class="text-start">{{ s.lastNameKana + s.firstNameKana }}</td>
                <td class="text-start">
                  <v-chip
                    v-if="s.gender == 'male'"
                    color="light-blue"
                    theme="dark"
                    size="small"
                  >男性</v-chip>
                  <v-chip
                    v-else
                    color="pink-lighten-2"
                    theme="dark"
                    size="small"
                  >女性</v-chip>
                </td>
                <td class="text-start">{{ s.assignDate }}</td>
                <td class="text-start">{{ s.retireDate ? s.retireDate : "" }}</td>
                <td class="text-start">{{ s.classesInCharge.length ? s.classesInCharge.find((c) => c.className)?.className : "" }}</td>
                <td class="text-start">
                  <v-btn @click="clickStaff(s)" color="primary" variant="flat" size="small">
                    編集
                  </v-btn>
                </td>
              </tr>
            </template>
          </draggable>
        </template>
      </v-table>
    </div>

    <v-dialog v-model="importDialog" max-width="600px">
      <v-card>
        <v-card-title>一括インポート</v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="4" align-self="center"> CSVファイル </v-col>
              <v-col cols="8" align-self="center">
                <v-file-input
                  v-model="importFile"
                  accept=".csv"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                >
                </v-file-input>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="importDialog = false" color="grey" variant="text">
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                @click="importCsv"
                :disabled="!importFile"
                :loading="loading"
                color="primary"
                variant="flat"
              >
                インポート
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import draggable from "vuedraggable";
import PageTitle from "@/components/atoms/PageTitle.vue";
import { staffModule } from "@/store/dataModules/staffModule";
import LocalDataService from "@/service/LocalDataService";
import { staffEditModule } from "@/store/dataModules/staffEditModule";
import { classModule } from "@/store/dataModules/classModule";
import dayjs from "dayjs";
import { StaffInfo } from "chaild-api/lib";
import ApiResource from "@/api/ApiResource";
import ApiStaff from "@/api/ApiStaff";

export default defineComponent({
  name: "StaffList",

  components: {
    PageTitle,
    draggable,
  },

  async mounted() {
    await this.listStaff();
    await this.listClass();
  },

  watch: {
    updateDialog: {
      handler(val) {
        if (!val) {
          staffEditModule.clearValues();
          this.listStaff();
        }
      },
    },
    async check(newValue: boolean) {
      (this as any).$store.commit("staff/setCheck", newValue);
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await (this as any).$store.dispatch("staff/listStaff", {
          nurseryId: String(nurseryId),
          check: newValue,
        });
      }
      this.loading = false;
    },
    // 💡 isHiddenOnly の監視も追加しておくのがベターです（チェックした瞬間に反映させるため）
    isHiddenOnly() {
      // computed の displayedStaff が自動で再評価されるため、APIを叩かなくても表示が切り替わります
    }
  },

  data() {
    return {
      loading: false,
      isHiddenOnly: false,
      check: false,
      importDialog: false,
      importFile: null as File | null,
    };
  },

  computed: {
    // API等から取得した生データ（全量）
    staff(): StaffInfo[] {
      return (this as any).$store.state.staff.staff || [];
    },
    
    // 画面表示用 ＆ Draggable用のリスト
    displayedStaff: {
      get(): StaffInfo[] {
        return this.staff.filter(s => {
          // 1. 退職者の判定（check が true なら退職日ありのみ、false なら退職日なしのみ）
          const matchRetire = this.check ? !!s.retireDate : !s.retireDate;

          // 2. 非表示設定の判定（isHiddenOnly が true なら非表示のみ、false なら表示のみ）
          const matchHidden = this.isHiddenOnly ? s.isHidden : !s.isHidden;

          // 両方の条件をクリアしたスタッフだけを画面に返す
          return matchRetire && matchHidden;
        });
      },
      set(newList: StaffInfo[]) {
        // ドラッグ＆ドロップ時の更新用
        (this as any).$store.state.staff.staff = newList; 
      }
    }
  },

  methods: {
    // 並び替え（ドラッグ）が終了した瞬間に発火するメソッド
    async onDragEnd() {
      // 画面上の新しい並び順になっている displayedStaff から ID だけを抽出
      const newStaffIds = this.displayedStaff.map((s) => s.userNurseryId);

      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        try {
          // 1. Storeに新しいIDの配列を保存
          (this as any).$store.commit("staff/setStaffIds", newStaffIds);
          
          // 2. 並び替え保存APIを実行
          await staffModule.sortStaffs(nurseryId);
          
          // 3. 念のため最新のリストを再取得（省略可能ですが確実です）
          await staffModule.listStaff({
            nurseryId: String(nurseryId),
            check: this.check,
          });
        } catch (e) {
          console.error("並び替えエラー", e);
        }
      }
      this.loading = false;
    },

    async listStaff() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await staffModule.listStaff({
          nurseryId: String(nurseryId),
          check: this.check,
        });
      }
      this.loading = false;
    },
    async getStaff(userId: string) {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await staffModule.getStaff({
          nurseryId: String(nurseryId),
          userNurseryId: userId,
        });
      }
    },
    async clickStaff(item: StaffInfo) {
      if (item) {
        staffEditModule.setUserId(String(item.userNurseryId));
        this.$router.push({ path: `/setting/staff/${item.userNurseryId}` });
      }
    },
    async listClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId: nurseryId,
          year: dayjs().year(),
        });
      }
    },
    createStaff() {
      this.$router.push({ path: "/setting/staff/new/edit" });
    },
    async importCsv() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (this.importFile && nurseryId) {
        const resourceResponse = await ApiResource.getPresignedUrl({
          imageType: "nurseryUserCsv",
          nurseryId,
          filename: this.importFile.name,
        }).catch();
        if (resourceResponse) {
          const uploadResponse = await ApiResource.uploadFile({
            url: resourceResponse.url,
            file: this.importFile,
          }).catch();
          if (uploadResponse) {
            const importResponse = await ApiStaff.importStaffCsv({
              nurseryId,
              filePath: resourceResponse.filePath,
            }).catch(() => {
              this.loading = false;
              this.importDialog = false;
            });
            if (importResponse) {
              alert("インポートが完了しました");
              this.importDialog = false;
              await this.listStaff();
            }
          }
        }
      }
      this.loading = false;
    },
  },
});
</script>

<style lang="scss" scoped>
.drag-handle {
  margin-bottom: 2px;
  vertical-align: middle;
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}
</style>